import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Pencil, Send, Trash2, Save, X, Unlock, Drama, Shield, Bot, User, Sparkles, Loader2 } from 'lucide-react';
import { ratingError } from '@/lib/owaspRating';
import RiskRatingField from './RiskRatingField';
import SeverityBadge from './SeverityBadge';
import type { VulnerabilityOut } from '@/services/questionsService';

export interface PendingVulnerabilityCardProps {
  draft: VulnerabilityOut;
  canEdit: boolean;
  onSave: (id: number, updated: Partial<VulnerabilityOut>) => void;
  onPush: (id: number) => void;
  onDiscard: (id: number) => void;
  isPushing: boolean;
  pushDisabledReason?: string | null;
  onEnrichWithAI?: (draft: VulnerabilityOut) => Promise<Record<string, string>>;
  isEnriching?: boolean;
}

export default function PendingVulnerabilityCard({
  draft,
  canEdit,
  onSave,
  onPush,
  onDiscard,
  isPushing,
  pushDisabledReason = null,
  onEnrichWithAI,
  isEnriching = false,
}: PendingVulnerabilityCardProps) {
  const [editMode, setEditMode] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftVulnerability, setDraftVulnerability] = useState('');
  const [draftScenario, setDraftScenario] = useState('');
  const [draftCountermeasure, setDraftCountermeasure] = useState('');
  const [draftRating, setDraftRating] = useState('');
  const [enrichError, setEnrichError] = useState<string | null>(null);

  const handleStartEdit = () => {
    setDraftName(draft.name);
    setDraftVulnerability(draft.vulnerability ?? '');
    setDraftScenario(draft.scenario ?? '');
    setDraftCountermeasure(draft.countermeasure ?? '');
    setDraftRating(draft.rating_vector_string ?? '');
    setEditMode(true);
  };

  const handleSaveEdit = () => {
    onSave(draft.id, {
      name: draftName,
      vulnerability: draftVulnerability || undefined,
      scenario: draftScenario || undefined,
      countermeasure: draftCountermeasure || undefined,
      rating_vector_string: draftRating.trim() || null,
    });
    setEditMode(false);
  };

  const handleEnrich = async () => {
    if (!onEnrichWithAI) return;
    setEnrichError(null);
    try {
      const current: VulnerabilityOut = {
        ...draft,
        name: draftName,
        vulnerability: draftVulnerability || null,
        scenario: draftScenario || null,
        countermeasure: draftCountermeasure || null,
        rating_vector_string: draftRating.trim() || null,
      };
      const result = await onEnrichWithAI(current);
      if (result.vulnerability !== undefined) setDraftVulnerability(result.vulnerability);
      if (result.scenario !== undefined) setDraftScenario(result.scenario);
      if (result.countermeasure !== undefined) setDraftCountermeasure(result.countermeasure);
      if (result.rating_vector_string !== undefined) setDraftRating(result.rating_vector_string);
    } catch (err) {
      setEnrichError(err instanceof Error ? err.message : 'AI enrichment failed');
    }
  };

  if (editMode) {
    return (
      <Card className="border-dashed border-amber-400 dark:border-amber-600">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-lg">Edit Draft</CardTitle>
            <div className="flex items-center gap-2">
              {onEnrichWithAI && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="outline" onClick={handleEnrich} disabled={isEnriching}>
                      {isEnriching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                      Generate with AI
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Generate vulnerability, scenario, countermeasure and risk rating using AI</TooltipContent>
                </Tooltip>
              )}
              <Button size="sm" onClick={handleSaveEdit} disabled={!draftName.trim() || !!ratingError(draftRating)}>
                <Save className="h-4 w-4 mr-1" />Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>
                <X className="h-4 w-4 mr-1" />Cancel
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {enrichError && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{enrichError}</div>
          )}
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Name</label>
            <Input value={draftName} onChange={(e) => setDraftName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Vulnerability</label>
            <Textarea value={draftVulnerability} onChange={(e) => setDraftVulnerability(e.target.value)} rows={3} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Threat Scenario</label>
            <Textarea value={draftScenario} onChange={(e) => setDraftScenario(e.target.value)} rows={3} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Countermeasure</label>
            <Textarea value={draftCountermeasure} onChange={(e) => setDraftCountermeasure(e.target.value)} rows={3} />
          </div>
          <RiskRatingField value={draftRating} onChange={setDraftRating} threatType={draft.threat_type} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-dashed border-amber-400 dark:border-amber-600">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {draft.external_id && (
                <span className="font-mono text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5 shrink-0">{draft.external_id}</span>
              )}
              <CardTitle className="text-lg">{draft.name}</CardTitle>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-700/10 dark:bg-amber-900/30 dark:text-amber-400">
                Pending
              </span>
              <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-600/10 dark:bg-slate-900/30 dark:text-slate-400">
                {draft.source === 'mcp-agent' ? <Bot className="h-3 w-3" /> : <User className="h-3 w-3" />}
                {draft.source === 'mcp-agent' ? 'Agent' : 'Manual'}
              </span>
              <SeverityBadge vector={draft.rating_vector_string} threatType={draft.threat_type} />
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-1 shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="icon" variant="ghost" aria-label="Edit draft" onClick={handleStartEdit}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit draft</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span title={pushDisabledReason ?? undefined} className="inline-flex">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Send to ThreatCat"
                      title={pushDisabledReason ?? 'Send to ThreatCat'}
                      onClick={() => {
                        if (pushDisabledReason) return;
                        onPush(draft.id);
                      }}
                      disabled={isPushing || Boolean(pushDisabledReason)}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{pushDisabledReason ?? 'Send to ThreatCat'}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="icon" variant="ghost" aria-label="Discard draft" className="text-destructive hover:text-destructive" onClick={() => onDiscard(draft.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Discard</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {draft.vulnerability && (
          <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Unlock className="h-4 w-4" />Vulnerability</div>
            <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
              {draft.vulnerability}
            </p>
          </div>
        )}
        {draft.scenario && (
          <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Drama className="h-4 w-4" />Threat Scenario</div>
            <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
              {draft.scenario}
            </p>
          </div>
        )}
        {draft.countermeasure && (
          <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Shield className="h-4 w-4" />Countermeasure</div>
            <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
              {draft.countermeasure}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
